import 'dayjs/locale/es';

import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ProspectsIcon } from '../assets';
import { Breadcrumbs } from '../components/common/Breadcrumbs';
import { TableSearchInput } from '../components/inputs/TableSearchInput';
import { CreateProspect } from '../components/popups/CreateProspect';
import DataTable from '../components/tables/DataTable';
import { getProspectsTableHeaders } from '../components/tables/headers/prospects';
import { replaceIds, ROUTES } from '../config/routes/config';
import { GET_PROSPECTS } from '../graphql/prospects/query';
import { useTableDataAndFilters } from '../hooks/useTableDataAndFilters';
import { prospectsFilterAtom } from '../states/atoms';
import type {
  BrandsFilters,
  BrandsTable,
  ResponseDataBrandList,
} from '../types';

dayjs.extend(localizedFormat);
dayjs.locale('es');

function Prospects() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const {
    resetPagination,
    total,
    filters,
    setFilters,
    table,
    error,
    loading,
    refetch,
  } = useTableDataAndFilters<
    BrandsFilters,
    BrandsTable,
    {},
    ResponseDataBrandList
  >({
    filterAtom: prospectsFilterAtom,
    getColumns: getProspectsTableHeaders,
    columnsExtras: {},
    responseAccessKey: 'brands',
    query: GET_PROSPECTS,
  });

  const handleTableClick = (id: string) => {
    navigate(
      replaceIds({
        url: ROUTES.SINGLE_PROSPECT,
        params: [{ id }],
      }),
    );
  };

  return (
    <div className="w-full flex flex-col gap-y-5">
      <Breadcrumbs
        icon={<ProspectsIcon />}
        breadcrumbs={[
          {
            text: t('prospects.title'),
          },
        ]}
      />
      <div className="flex flex-col gap-y-4">
        <div className="flex flex-row justify-between">
          <div>
            <TableSearchInput
              placeholder={t('prospects.searchInputPlaceholder')}
              filters={{ ...filters }}
              setFilters={(val) => {
                setFilters(val);
                resetPagination();
              }}
              searchKey="name"
            />
          </div>
          <div className="flex flex-row gap-x-2">
            <CreateProspect refetch={refetch} />
          </div>
        </div>
        <DataTable
          error={error}
          handleRowClick={(id) => handleTableClick(id)}
          loading={loading}
          table={table}
          total={total}
          tableId="ProspectsTable"
          sorting={{ ...filters }}
        />
      </div>
    </div>
  );
}

export default Prospects;
